import React, { useState } from "react";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import { FileText, Image as ImageIcon, MapPin, X } from "lucide-react";
import CustomSelect from "../../../../components/common/CustomSelect/CustomSelect";
import CustomDatePicker from "../../../../components/common/CustomDatePicker/CustomDatePicker";
import CustomTimePicker from "../../../../components/common/CustomTimePicker/CustomTimePicker";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import { BasicsVenuesTabProps } from "./ComponentPropsV3";

if (Quill) {
  const Icons = Quill.import("ui/icons");
  if (Icons) {
    Icons["undo"] = `<svg viewBox="0 0 18 18"><polygon class="ql-fill ql-stroke" points="6 9 3 6 6 3 6 9"/><path class="ql-stroke" d="M4.5,6H11a3.5,3.5,0,0,1,0,7H9"/></svg>`;
    Icons["redo"] = `<svg viewBox="0 0 18 18"><polygon class="ql-fill ql-stroke" points="12 9 15 6 12 3 12 9"/><path class="ql-stroke" d="M13.5,6H7a3.5,3.5,0,0,0,0,7H9"/></svg>`;
  }
}

const quillModules = {
  toolbar: {
    container: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ["bold", "italic", "underline"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ align: [] }],
      ["link"],
      ["undo", "redo"],
    ],
    handlers: {
      undo: function (this: any) {
        this.quill.history.undo();
      },
      redo: function (this: any) {
        this.quill.history.redo();
      },
    },
  },
  history: {
    delay: 500,
    maxStack: 100,
    userOnly: true,
  },
};

const isDescriptionEmpty = (html: string) => {
  if (!html) return true;
  const stripped = html.replace(/<[^>]*>/g, "").trim();
  return stripped === "";
};

const PosterControls: React.FC<{ inputId: string; posterImage: string | null; setPosterImage: (val: string | null) => void; setPosterFile: (val: File | null) => void; setShowCropModal: (val: boolean) => void }> = ({
  inputId,
  posterImage,
  setPosterImage,
  setPosterFile,
  setShowCropModal,
}) => (
  <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", padding: 16, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 16, gap: 16, width: "100%", height: "100%", boxSizing: "border-box" }}>
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <span style={{ fontSize: 10, fontFamily: "monospace", color: "#F79B59", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 600 }}>Poster Controls</span>
      {!posterImage ? (
        <button type="button" onClick={() => document.getElementById(inputId)?.click()} className="v3-btn-primary" style={{ fontSize: 12, padding: "10px 16px" }}>
          <ImageIcon size={14} /> Upload Image
        </button>
      ) : (
        <>
          <button type="button" onClick={() => document.getElementById(inputId)?.click()} className="v3-btn-secondary" style={{ fontSize: 11 }}>Replace Poster</button>
          <button type="button" onClick={() => setShowCropModal(true)} className="v3-btn-secondary" style={{ fontSize: 11 }}>Crop Poster</button>
          <button type="button" onClick={() => { setPosterImage(null); setPosterFile(null); }} className="v3-btn-secondary" style={{ fontSize: 11, color: "#FF5D73", borderColor: "rgba(255,93,115,0.2)" }}>Remove Poster</button>
        </>
      )}
    </div>
    <div style={{ borderTop: "1px solid rgba(255,255,255,0.05)", paddingTop: 12 }}>
      <p style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", fontStyle: "italic", marginBottom: 4 }}>It is preferred to have a 3:4 portrait or 4:3 portrait ratio image.</p>
      <p style={{ fontSize: 9, fontFamily: "monospace", color: "rgba(238,63,124,0.7)", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 600, margin: 0 }}>Optimal: 1200 x 1600px</p>
    </div>
  </div>
);

const BasicsVenuesTabV3: React.FC<BasicsVenuesTabProps> = ({
  builderMode,
  name,
  setName,
  category,
  setCategory,
  categoriesList,
  isSingleDay,
  setIsSingleDay,
  singleDate,
  setSingleDate,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  startTime,
  setStartTime,
  endTime,
  setEndTime,
  posterImage,
  setPosterImage,
  setPosterFile,
  setShowCropModal,
  cropScale,
  description,
  setDescription,
  shortDescription,
  setShortDescription,
  venueName,
  setVenueName,
  venueLocationSearch,
  setVenueLocationSearch,
  showVenueSuggestions,
  setShowVenueSuggestions,
  selectedLocationDetail,
  setSelectedLocationDetail,
  isCustomVenue,
  setIsCustomVenue,
  hasMultipleVenues,
  setHasMultipleVenues,
  secondaryVenues,
  setSecondaryVenues,
  newSecName,
  setNewSecName,
  newSecLoc,
  setNewSecLoc,
  showAddSecondaryVenue,
  setShowAddSecondaryVenue,
  isNightMode,
  posterInputRef,
  showErrors,
}) => {
  const [localTouched, setLocalTouched] = useState<{ name?: boolean; shortDescription?: boolean; description?: boolean }>({});

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPosterFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setPosterImage(reader.result as string);
    reader.readAsDataURL(file);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Event Information */}
      <div className="glass-panel" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div className="v3-section-head">
          <FileText size={14} color="#EE3F7C" /> Event Information
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {builderMode === "quick" ? (
            <>
              {/* Event Name * */}
              <div>
                <label className="v3-label">Event Name *</label>
                <input
                  type="text"
                  className={`v3-input ${(showErrors || localTouched.name) && !name.trim() ? "is-invalid" : ""}`}
                  placeholder="e.g. San Francisco Tech Summit 2026"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onBlur={() => setLocalTouched((prev) => ({ ...prev, name: true }))}
                />
                {((showErrors || localTouched.name) && !name.trim()) && (
                  <div style={{ color: "#FF5D73", fontSize: 12, marginTop: 4, fontWeight: 500 }}>
                    Event Name is required
                  </div>
                )}
              </div>

              {/* Short Share Description (SEO) * */}
              <div>
                <label className="v3-label">Short Share Description (SEO) *</label>
                <input
                  type="text"
                  className={`v3-input ${(showErrors || localTouched.shortDescription) && !shortDescription.trim() ? "is-invalid" : ""}`}
                  placeholder="Enter a short description for search engines, social sharing, and event previews."
                  value={shortDescription}
                  onChange={(e) => setShortDescription(e.target.value)}
                  onBlur={() => setLocalTouched((prev) => ({ ...prev, shortDescription: true }))}
                />
                {((showErrors || localTouched.shortDescription) && !shortDescription.trim()) && (
                  <div style={{ color: "#FF5D73", fontSize: 12, marginTop: 4, fontWeight: 500 }}>
                    Short Share Description is required
                  </div>
                )}
              </div>
            </>
          ) : (
            <div>
              <label className="v3-label">Event Name</label>
              <input type="text" className="v3-input" placeholder="e.g. SF Design Week Gala" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
            <div>
              <label className="v3-label">Event Category</label>
              <CustomSelect
                options={
                  categoriesList.length > 0
                    ? categoriesList.map((c: any) => {
                        const cName = c.categoryName || c.name || c.category || String(c);
                        return { label: cName, value: cName };
                      })
                    : [
                        "Arts", "Business", "Coaching and Consulting", "Community and Culture",
                        "Entrepreneurship", "Education and Training", "Family and Friends",
                        "Fashion and Beauty", "Film and Entertainment", "Food and Drink",
                        "Government and Politics", "Health and Wellbeing", "Hobbies and Interest",
                        "Music and Theater", "Religion and Spirituality", "Science and Technology",
                        "Sports and Fitness", "Travel and Outdoor", "Visual Arts", "Others", "Music",
                        "Technology & Design"
                      ].map((v) => ({ label: v, value: v }))
                }
                value={category || "Technology & Design"}
                onChange={(val: any) => setCategory(val)}
                placeholder="Select Category"
              />
            </div>
            <div>
              <label className="v3-label">Duration Structure</label>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  onClick={() => setIsSingleDay(true)}
                  style={{
                    flex: 1, padding: "8px 0", fontSize: 10, fontWeight: 700,
                    borderRadius: 8, cursor: "pointer", transition: "all 0.15s",
                    background: isSingleDay ? "rgba(238, 63, 124, 0.15)" : "rgba(255,255,255,0.05)",
                    border: isSingleDay ? "1px solid #EE3F7C" : "1px solid rgba(255,255,255,0.05)",
                    color: isSingleDay ? "#ffffff" : "rgba(255,255,255,0.6)",
                  }}
                >Single-Day</button>
                <button
                  type="button"
                  onClick={() => setIsSingleDay(false)}
                  style={{
                    flex: 1, padding: "8px 0", fontSize: 10, fontWeight: 700,
                    borderRadius: 8, cursor: "pointer", transition: "all 0.15s",
                    background: !isSingleDay ? "rgba(238, 63, 124, 0.15)" : "rgba(255,255,255,0.05)",
                    border: !isSingleDay ? "1px solid #EE3F7C" : "1px solid rgba(255,255,255,0.05)",
                    color: !isSingleDay ? "#ffffff" : "rgba(255,255,255,0.6)",
                  }}
                >Multi-Day</button>
              </div>
            </div>
          </div>

          {/* Date Picker Integration */}
          {isSingleDay ? (
            <div>
              <label className="v3-label">Event Date</label>
              <CustomDatePicker value={singleDate} onChange={(val) => setSingleDate(val)} placeholder="Select event date" />
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
              <div>
                <label className="v3-label">Start Date</label>
                <CustomDatePicker value={startDate} onChange={(val) => setStartDate(val)} placeholder="Select start date" />
              </div>
              <div>
                <label className="v3-label">End Date</label>
                <CustomDatePicker value={endDate} onChange={(val) => setEndDate(val)} placeholder="Select end date" />
              </div>
            </div>
          )}

          {/* Time Picker Integration */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
            <div>
              <label className="v3-label">Doors Open</label>
              <CustomTimePicker value={startTime} onChange={(val) => setStartTime(val)} placeholder="Select opening time" />
            </div>
            <div>
              <label className="v3-label">Doors Close</label>
              <CustomTimePicker value={endTime} onChange={(val) => setEndTime(val)} placeholder="Select closing time" />
            </div>
          </div>
        </div>
      </div>

      {/* Poster */}
      <div className="glass-panel" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div className="v3-section-head">
          <ImageIcon size={14} color="#EE3F7C" /> Event Cover Poster
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "stretch", width: "100%" }}>
          <div style={{ flex: "1 1 240px", minWidth: 0 }}>
            <div className="v3-poster-box" onClick={() => posterInputRef.current?.click()}>
              {posterImage ? (
                <img src={posterImage} alt="Cover Preview" style={{ transform: `scale(${cropScale})` }} />
              ) : (
                <div style={{ textAlign: "center", padding: 24, display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
                  <ImageIcon size={44} color="rgba(255,255,255,0.2)" />
                  <div>
                    <span style={{ fontSize: 11, fontFamily: "monospace", color: "#fff", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, display: "block" }}>No Cover Poster Selected</span>
                    <span style={{ fontSize: 10, color: "rgba(255,255,255,0.35)", display: "block", marginTop: 4 }}>Click anywhere in this box to upload your image</span>
                  </div>
                </div>
              )}
            </div>
            <input ref={posterInputRef} id="poster-file-selector-v3" type="file" accept="image/*" style={{ display: "none" }} onChange={handleFileChange} />
          </div>
          <div style={{ width: 220, flexShrink: 0, display: "flex" }}>
            <PosterControls
              inputId="poster-file-selector-v3"
              posterImage={posterImage}
              setPosterImage={setPosterImage}
              setPosterFile={setPosterFile}
              setShowCropModal={setShowCropModal}
            />
          </div>
        </div>
      </div>

      {/* Description (Full Mode Only) */}
      {builderMode === "full" && (
        <div className="glass-panel" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div className="v3-section-head">
            <FileText size={14} color="#EE3F7C" /> Event Descriptions
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <label className="v3-label">Main Description</label>
              <textarea rows={5} className="v3-input" placeholder="Write a compelling overview of your event schedule, topics, speakers, or features..." value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
            <div>
              <label className="v3-label">Short Share Description</label>
              <input type="text" className="v3-input" placeholder="A short, catchy line describing the event..." value={shortDescription} onChange={(e) => setShortDescription(e.target.value)} />
              <p style={{ fontSize: 10, color: "rgba(255,255,255,0.35)", marginTop: 4, marginBottom: 0 }}>This summary will display on social platforms and Google when sharing your booking link.</p>
            </div>
          </div>
        </div>
      )}

      {/* Venue */}
      <div className="glass-panel" style={{ display: "flex", flexDirection: "column", gap: 16, position: "relative", zIndex: 30 }}>
        <div className="v3-section-head">
          <MapPin size={14} color="#EE3F7C" /> {builderMode === "full" ? "Venue & Location Settings" : "Event Venue & Location"}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {builderMode === "full" && setHasMultipleVenues && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid rgba(255,255,255,0.06)", paddingBottom: 12, marginBottom: 4 }}>
              <div>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#fff", display: "block" }}>Do you have multiple venues?</span>
                <span style={{ fontSize: 11, color: "rgba(255,255,255,0.45)" }}>Toggle to enable adding multiple venues for your event.</span>
              </div>
              <button
                type="button"
                className={`v3-toggle ${hasMultipleVenues ? "on" : "off"}`}
                onClick={() => setHasMultipleVenues(!hasMultipleVenues)}
              >
                <div className="v3-toggle-knob" />
              </button>
            </div>
          )}

          {!isCustomVenue && (
            <div>
              <label className="v3-label">Search Predefined Venue or Address</label>
              <GooglePlacesAutocomplete
                apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ""}
                selectProps={{
                  placeholder: "Search for a venue, address, or landmark...",
                  isClearable: true,
                  value: venueLocationSearch ? { label: venueLocationSearch, value: { description: venueLocationSearch } } : null,
                  onChange: (place: any) => {
                    if (place) {
                      const selectedAddr = place.label || place.value?.description || "";
                      setVenueLocationSearch(selectedAddr);
                      const mainName = place.value?.structured_formatting?.main_text || selectedAddr.split(',')[0];
                      setVenueName(mainName);
                      setSelectedLocationDetail({
                        name: mainName,
                        address: selectedAddr,
                        coordinates: "Google Maps Verified",
                      });
                    } else {
                      setVenueLocationSearch("");
                      setVenueName("");
                      setSelectedLocationDetail(null);
                    }
                  },
                  styles: {
                    control: (provided: any, state: any) => ({
                      ...provided,
                      backgroundColor: isNightMode ? "rgba(255, 255, 255, 0.05)" : "#ffffff",
                      borderColor: state.isFocused ? "#EE3F7C" : isNightMode ? "rgba(255, 255, 255, 0.05)" : "#cbd5e1",
                      borderRadius: "12px",
                      minHeight: "44px",
                      boxShadow: "none",
                      color: isNightMode ? "#ffffff" : "#0f172a",
                      "&:hover": { borderColor: "rgba(238, 63, 124, 0.4)" },
                    }),
                    input: (provided: any) => ({ ...provided, color: isNightMode ? "#ffffff" : "#0f172a", fontSize: "12px" }),
                    singleValue: (provided: any) => ({ ...provided, color: isNightMode ? "#ffffff" : "#0f172a", fontSize: "12px" }),
                    placeholder: (provided: any) => ({ ...provided, color: isNightMode ? "rgba(255, 255, 255, 0.35)" : "#94a3b8", fontSize: "12px" }),
                    menu: (provided: any) => ({
                      ...provided,
                      backgroundColor: isNightMode ? "rgba(18, 18, 28, 0.98)" : "#ffffff",
                      backdropFilter: "blur(24px)",
                      border: isNightMode ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid #e2e8f0",
                      borderRadius: "14px",
                      zIndex: 99999,
                      padding: "6px",
                      boxShadow: "0 20px 48px rgba(0, 0, 0, 0.5)",
                    }),
                    option: (provided: any, state: any) => ({
                      ...provided,
                      backgroundColor: state.isSelected ? "rgba(238, 63, 124, 0.2)" : state.isFocused ? (isNightMode ? "rgba(255, 255, 255, 0.08)" : "#f1f5f9") : "transparent",
                      color: isNightMode ? "#ffffff" : "#0f172a",
                      fontSize: "12px",
                      borderRadius: "8px",
                      cursor: "pointer",
                    }),
                  },
                }}
              />
            </div>
          )}

          <button type="button" className="v3-btn-ghost" onClick={() => { const newMode = !isCustomVenue; setIsCustomVenue(newMode); setSelectedLocationDetail(null); setVenueName(""); setVenueLocationSearch(""); }}>
            {isCustomVenue ? "← Switch back to Predefined Venues" : "+ Or specify custom venue name & location manually"}
          </button>

          {isCustomVenue && (
            <div style={{ padding: 16, borderRadius: 12, border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.02)", display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
                <div>
                  <label className="v3-label">Custom Venue Name</label>
                  <input type="text" className="v3-input" placeholder="e.g. My Custom Banquet Hall" value={venueName} onChange={(e) => setVenueName(e.target.value)} />
                </div>
                <div>
                  <label className="v3-label">Custom Venue Address</label>
                  <input type="text" className="v3-input" placeholder="e.g. 555 Galaxy Blvd, San Francisco, CA" value={venueLocationSearch} onChange={(e) => setVenueLocationSearch(e.target.value)} />
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, paddingTop: 8, borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                <span style={{ fontSize: 10, color: "rgba(255,255,255,0.4)" }}>Directly lookup coordinates or address pin:</span>
                <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${venueName} ${venueLocationSearch}`)}`} target="_blank" rel="noopener noreferrer" style={{ padding: "6px 14px", background: "rgba(238,63,124,0.12)", border: "1px solid rgba(238,63,124,0.3)", borderRadius: 10, color: "#EE3F7C", fontSize: 12, fontFamily: "monospace", textDecoration: "none", display: "flex", alignItems: "center", gap: 6 }}>
                  <MapPin size={12} /> Pin / Search on Google Maps
                </a>
              </div>
            </div>
          )}

          {(selectedLocationDetail || (isCustomVenue && (venueName || venueLocationSearch))) && (
            <div style={{ padding: 16, borderRadius: 12, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                <div style={{ width: 64, height: 64, borderRadius: 8, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", flexShrink: 0, position: "relative", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <img src="https://images.unsplash.com/photo-1569336415962-a4bd9f69cd83?w=200&auto=format&fit=crop&q=80" alt="Map" style={{ width: "100%", height: "100%", objectFit: "cover", filter: "brightness(0.7) contrast(1.25)" }} />
                  <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <MapPin size={20} color="#EE3F7C" />
                  </div>
                </div>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: "#fff" }}>{venueName || selectedLocationDetail?.name || "Unnamed Venue"}</span>
                    <span style={{ fontSize: 9, fontFamily: "monospace", padding: "2px 6px", borderRadius: 4, border: "1px solid rgba(255,255,255,0.07)", background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.45)" }}>{isCustomVenue ? "Custom" : "Predefined"}</span>
                  </div>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", marginTop: 2 }}>{venueLocationSearch || selectedLocationDetail?.address}</div>
                  {selectedLocationDetail && <div style={{ fontSize: 10, fontFamily: "monospace", color: "#F79B59", marginTop: 4 }}>{selectedLocationDetail.coordinates}</div>}
                </div>
              </div>
              <button type="button" onClick={() => { setSelectedLocationDetail(null); setVenueLocationSearch(""); setVenueName(""); if (isCustomVenue) setIsCustomVenue(false); }} style={{ padding: 6, borderRadius: 8, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", cursor: "pointer", color: "rgba(255,255,255,0.5)" }}>
                <X size={12} />
              </button>
            </div>
          )}

          {/* Secondary Venues (Full mode only) */}
          {builderMode === "full" && (hasMultipleVenues === undefined || hasMultipleVenues) && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 11, fontFamily: "monospace", color: "rgba(255,255,255,0.45)", textTransform: "uppercase", letterSpacing: "0.07em" }}>Secondary Session Venues</span>
                <button type="button" className="v3-btn-ghost" onClick={() => setShowAddSecondaryVenue(!showAddSecondaryVenue)}>+ Add Session Venue</button>
              </div>
              {showAddSecondaryVenue && (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <input type="text" className="v3-input" style={{ flex: 1, minWidth: 120 }} placeholder="Venue Name" value={newSecName} onChange={(e) => setNewSecName(e.target.value)} />
                  <input type="text" className="v3-input" style={{ flex: 1, minWidth: 120 }} placeholder="Location" value={newSecLoc} onChange={(e) => setNewSecLoc(e.target.value)} />
                  <button type="button" className="v3-btn-primary" style={{ padding: "10px 14px", fontSize: 12 }} onClick={() => { if (!newSecName || !newSecLoc) return; setSecondaryVenues([...secondaryVenues, { id: `sec-${Date.now()}`, name: newSecName, location: newSecLoc }]); setNewSecName(""); setNewSecLoc(""); setShowAddSecondaryVenue(false); }}>Add</button>
                </div>
              )}
              {secondaryVenues.map((sec) => (
                <div key={sec.id} style={{ padding: "10px 14px", borderRadius: 10, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)", display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, color: "#fff" }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <MapPin size={12} color="#F79B59" /> <strong>{sec.name}</strong> <span style={{ color: "rgba(255,255,255,0.45)" }}>({sec.location})</span>
                  </span>
                  <button type="button" onClick={() => setSecondaryVenues(secondaryVenues.filter((v) => v.id !== sec.id))} style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.4)", padding: 4 }}><X size={12} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BasicsVenuesTabV3;
